import { useRoomSession } from '../context/room_session';
import { hasWorldSubject } from './use_camera_subject';
import { ViewMode } from './use_view_mode';
import { useObservable, useVideoEnabled } from '../';
import { useMultiplexer } from './use_multiplexer';
import { isHostRoomSession } from '@streem/sdk-core';
import { scan } from 'rxjs/operators';
export const emptyDisplayModeResult = (remoteUserName) => ({
    // for ONSITE, in customer view, we will only have local.
    local: {
        switches: {
            showMicIndicator: false,
            inPip: false,
            showAvatar: false,
        },
        properties: {
            icon: 'Face',
            message: 'Your camera is off',
        },
        styles: {
            width: 'inherit',
            height: 'inherit',
        },
    },
    remote: {
        // for ONSITE, in expert view, we will only have remote.
        switches: {
            showMicIndicator: true,
            inPip: false,
            showAvatar: false,
        },
        properties: {
            icon: 'Face',
            message: remoteUserName
                ? `${remoteUserName} has turned off their camera`
                : 'They turned their camera off',
        },
        styles: {
            width: '187px',
            height: '--base-height',
        },
    },
});
/**
 * Returns object of shape DisplayModeResult
 *
 * @author noah.stolmaker@streem.com
 *
 * HEY YOU! PLEASE DON'T BE AFRAID TO EXPAND THIS HOOK! THIS IS A GOOD PLACE TO DO ADD FURTHER UI SWITCHES AND STYLES!
 *
 * @description this function slurps in what we know about the video that we're rendering,
 * and produces a DisplayModeResult object which has clear style definitions for what each
 * the UI should look like, in each possible configuration.
 *
 * E.g. If Camera is in WORLD_MODE, but remote (expert/spectator) video is off, which screen
 * should be fullscreen and which should be in PiP mode? Do we show a mic indicator or not?
 * The idea is that this object can be passed down through props and all components can just
 * reference this one object, rather than putting so much if/else logic into every component.

    @param videoController - basically the video stream instance that you're concerned with. What gets painted to a canvas.
    @param canvasContainer - The container which will house the video's <canvas> element.
    Optional, but If you leave this out, it'll assume 1280x720. Some style calculations could be broken, but the UI properties
    like isPip and icon should be calculated just fine.
 */
// TODO: when we add the loading spinner for the spectator (for when the camera flip takes several seconds) the ONSITE viewmode will be helpful. We can rename it if ONSITE doesn't make sense. All ONSITE means is that we have one video controller. When we only have one video controller the auto grid only has one child and that video fills the screen. For one-way and onsite calls this is what we want.
export const useDetermineUIMode = ({ videoController, canvasContainer = undefined, }) => {
    const roomSession = useRoomSession();
    const viewMode = hasWorldSubject(roomSession.media.videoControllers)
        ? ViewMode.FACE_TO_WORLD
        : ViewMode.FACE_TO_FACE;
    const isHost = isHostRoomSession(roomSession);
    /** Multiplexer */
    const canvasDims = useMultiplexer(roomSession, videoController, canvasContainer) ?? {
        width: 1280,
        height: 720,
    };
    // data about landscape vs portrait mode/aspect ratio
    const isPortrait = canvasDims ? canvasDims.height > canvasDims.width : false;
    const aspectRatioConversion = isPortrait
        ? canvasDims.width / canvasDims.height
        : canvasDims.height / canvasDims.width;
    const isLocalOrRemoteVersion = videoController.ownerIsLocalUser ? 'local' : 'remote';
    const [remoteUser] = useObservable(roomSession.user.remoteUser);
    const [localUser] = useObservable(roomSession.user.localUser);
    const [videoBackgrounded] = useObservable(videoController.backgrounded);
    const [paused] = useObservable(videoController.paused);
    const [remoteVideoEnabled] = useVideoEnabled(roomSession.media.videoControllers.pipe(scan((acc, controllers) => {
        return controllers.find(controller => !controller.ownerIsLocalUser) || acc;
    }, undefined)));
    const userName = remoteUser?.name || 'The customer';
    const backgroundedMessage = `
        ${userName} has backgrounded the Streem call. \n
        Their video, and possibly microphone, are unavailable, but they can hear you.
        `;
    const cameraOffMessage = `${userName} has turned off their camera`;
    const lowBandwidthMessage = `${userName}'s connection is weak, video has been disabled. Audio remains on.`;
    function determineDisplayMessage() {
        if (paused) {
            return lowBandwidthMessage;
        }
        else if (videoBackgrounded) {
            return backgroundedMessage;
        }
        else {
            return cameraOffMessage;
        }
    }
    let displayMode;
    if (isHost) {
        // HOST
        // 2WV enabled -
        // face to FACE:
        // NOTE: only when the call is active, before the remote user answers local video should be full screen
        // Local - PiP, camera muted is icon.
        // Remote - full, camera muted is avatar.
        // face to WORLD:
        // Local full, camera muted is icon.
        // Remote PiP, camera muted hides PiP.
        switch (viewMode) {
            case ViewMode.FACE_TO_FACE:
                displayMode = {
                    local: {
                        styles: remoteUser
                            ? {
                                height: isPortrait
                                    ? 'var(--base-height)'
                                    : `calc(var(--base-height) * ${aspectRatioConversion})`,
                                width: isPortrait
                                    ? `calc(var(--base-height) * ${aspectRatioConversion})`
                                    : 'var(--base-height)',
                            }
                            : {
                                height: 'inherit',
                                width: 'inherit',
                            },
                        properties: {
                            icon: 'Face',
                            message: !remoteUser ? 'Your camera is off' : '',
                        },
                        switches: {
                            showMicIndicator: false,
                            inPip: !!remoteUser,
                            showAvatar: false,
                        },
                    },
                    remote: {
                        switches: {
                            showMicIndicator: true,
                            inPip: false,
                            showAvatar: true,
                        },
                        properties: {
                            avatarSrc: remoteUser?.avatarUrl,
                            displayName: remoteUser?.name,
                            // We only care about the paused state here. If the remote user backgrounded their video, we want to show the avatar.
                            message: paused ? lowBandwidthMessage : '',
                        },
                        styles: {
                            height: 'inherit',
                            width: 'inherit',
                            fontColor: '#A9AEB4',
                            ...(paused ? { fontSize: '5.5vw' } : {}),
                        },
                    },
                };
                break;
            case ViewMode.FACE_TO_WORLD:
                displayMode = {
                    local: {
                        styles: {
                            height: 'inherit',
                            width: 'inherit',
                        },
                        properties: {
                            icon: 'CameraOffIcon',
                            message: 'Your rear-facing camera is off',
                        },
                        switches: {
                            inPip: false,
                            showMicIndicator: false,
                            showAvatar: false,
                        },
                    },
                    remote: {
                        switches: {
                            inPip: true,
                            showMicIndicator: true,
                            showAvatar: true,
                        },
                        properties: {
                            avatarSrc: remoteUser?.avatarUrl,
                            displayName: remoteUser?.name,
                            // We only care about the paused state here. If the remote user backgrounded their video, we want to show the avatar.
                            message: paused ? lowBandwidthMessage : '',
                        },
                        styles: {
                            height: isPortrait
                                ? 'var(--base-height)'
                                : `calc(var(--base-height) * ${aspectRatioConversion})`,
                            width: isPortrait
                                ? `calc(var(--base-height) * ${aspectRatioConversion})`
                                : 'var(--base-height)',
                            fontColor: 'white',
                            ...(paused ? { fontSize: '12px' } : {}),
                        },
                    },
                };
                break;
            default:
                // defaults to ONSITE
                // 2WV disabled (one video controller, view mode is ONSITE) - local, environemnt facing camera only. Full. Camera muted is icon.
                displayMode = emptyDisplayModeResult(remoteUser?.name);
                break;
        }
    }
    else {
        // SPECTATOR
        // 2WV enabled -
        // face to FACE:
        // Local full, camera muted is icon.
        // Remote full, camera muted is avatar.
        // Videos are stacked. Remote should always appear on top or to the left.
        // face to WORLD:
        // Remote full, camera muted is icon.
        // Local PiP, camera muted hides PiP.
        switch (viewMode) {
            case ViewMode.FACE_TO_FACE:
                displayMode = {
                    local: {
                        styles: {
                            height: isPortrait
                                ? 'var(--base-height)'
                                : `calc(var(--base-height) * ${aspectRatioConversion})`,
                            width: isPortrait
                                ? `calc(var(--base-height) * ${aspectRatioConversion})`
                                : 'var(--base-height)',
                        },
                        properties: {
                            avatarSrc: localUser?.avatarUrl,
                            displayName: localUser?.name,
                        },
                        switches: {
                            showMicIndicator: true,
                            inPip: false,
                            showAvatar: true,
                        },
                    },
                    remote: {
                        switches: {
                            showMicIndicator: true,
                            inPip: false,
                            showAvatar: false,
                        },
                        properties: {
                            icon: 'Face',
                            message: determineDisplayMessage(),
                        },
                        styles: {
                            height: 'inherit',
                            width: 'inherit',
                            ...(paused ? { fontColor: 'white' } : {}),
                        },
                    },
                };
                break;
            case ViewMode.FACE_TO_WORLD:
                displayMode = {
                    local: {
                        styles: {
                            height: 'inherit',
                            width: 'inherit',
                        },
                        properties: {
                            avatarSrc: localUser?.avatarUrl,
                            displayName: localUser?.name,
                        },
                        switches: {
                            inPip: true,
                            showMicIndicator: true,
                            showAvatar: true,
                        },
                    },
                    remote: {
                        switches: {
                            inPip: false,
                            showMicIndicator: true,
                            showAvatar: false,
                        },
                        properties: {
                            icon: 'CameraOffIcon',
                            message: determineDisplayMessage(),
                        },
                        styles: {
                            height: isPortrait
                                ? 'var(--base-height)'
                                : `calc(var(--base-height) * ${aspectRatioConversion})`,
                            width: isPortrait
                                ? `calc(var(--base-height) * ${aspectRatioConversion})`
                                : 'var(--base-height)',
                            ...(paused ? { fontSize: '20px' } : {}),
                        },
                    },
                };
                break;
            default:
                // defaults to ONSITE
                // 2WV disabled (one video controller, view mode is ONSITE) - remote, environment facing camera only. Full. Camera muted is icon.
                displayMode = emptyDisplayModeResult(remoteUser?.name);
                break;
        }
    }
    const localOrRemoteDisplayMode = displayMode[isLocalOrRemoteVersion];
    return {
        ...localOrRemoteDisplayMode,
        videoOwner: isLocalOrRemoteVersion,
        raw: {
            roomSession,
            viewMode,
            remoteVideoEnabled,
            canvasDims,
            isPortrait,
            aspectRatioConversion,
        },
    };
};
//# sourceMappingURL=use_determine_ui_mode.js.map